"use client";

import {
  Button,
  Checkbox,
  Input,
  Label,
  Loader,
  cn,
  useDebounceCallback,
} from "@devhop/ui";
import { Package, Search, Tags } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  LinkableCatalogProductRow,
  LinkableCatalogSkuRow,
  ShopMasterCategoryOption,
} from "../api/client";
import { useLinkableCatalogQuery } from "../api/queries";
import { CascadingCategorySelect } from "./CascadingCategorySelect";

type ShopAddSellablePickCatalogPanelProps = {
  shopId: string;
  categories: ShopMasterCategoryOption[];
  categoriesLoading: boolean;
  categoryId: string;
  onCategoryChange: (id: string) => void;
  selectedProductId: string | null;
  onSelectProduct: (productId: string | null) => void;
  checkedSkus: LinkableCatalogSkuRow[];
  onCheckedSkusChange: (skus: LinkableCatalogSkuRow[]) => void;
};

function conditionLabel(condition: "new" | "used" | null) {
  if (condition === "used") return "ມືສອງ";
  if (condition === "new") return "ໃໝ່";
  return "—";
}

export function ShopAddSellablePickCatalogPanel({
  shopId,
  categories,
  categoriesLoading,
  categoryId,
  onCategoryChange,
  selectedProductId,
  onSelectProduct,
  checkedSkus,
  onCheckedSkusChange,
}: ShopAddSellablePickCatalogPanelProps) {
  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debouncedSyncQ = useDebounceCallback((val: string) => {
    setDebouncedQ(val.trim());
  }, 350);

  useEffect(() => {
    debouncedSyncQ(search);
  }, [search, debouncedSyncQ]);

  const linkableQuery = useLinkableCatalogQuery(
    shopId,
    { categoryId, q: debouncedQ || undefined },
    Boolean(categoryId),
  );

  const products = linkableQuery.data?.items ?? [];

  const selectedProduct = useMemo(
    () =>
      products.find((p) => p.catalogProductId === selectedProductId) ?? null,
    [products, selectedProductId],
  );

  const selectableSkus = useMemo(
    () => selectedProduct?.skus.filter((s) => !s.alreadyLinked) ?? [],
    [selectedProduct],
  );

  const checkedIds = useMemo(
    () => new Set(checkedSkus.map((s) => s.catalogSkuId)),
    [checkedSkus],
  );

  const toggleSku = (sku: LinkableCatalogSkuRow, checked: boolean) => {
    if (checked) {
      if (checkedIds.has(sku.catalogSkuId)) return;
      onCheckedSkusChange([...checkedSkus, sku]);
      return;
    }
    onCheckedSkusChange(
      checkedSkus.filter((s) => s.catalogSkuId !== sku.catalogSkuId),
    );
  };

  const toggleAllSelectable = (checked: boolean) => {
    if (!checked) {
      onCheckedSkusChange([]);
      return;
    }
    onCheckedSkusChange([...selectableSkus]);
  };

  const allSelectableChecked =
    selectableSkus.length > 0 &&
    selectableSkus.every((s) => checkedIds.has(s.catalogSkuId));

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <Label>
          {"ໝວດສິນຄ້າ"} <span className="text-destructive">*</span>
        </Label>
        <CascadingCategorySelect
          categories={categories}
          value={categoryId}
          onChange={(val) => {
            onCategoryChange(val);
            onSelectProduct(null);
            onCheckedSkusChange([]);
          }}
          isLoading={categoriesLoading}
          clearable
          requireLeaf
        />
        <p className="text-muted-foreground text-xs">
          {"ເລືອກໝວດກ່ອນ ແລ້ວເລືອກສິນຄ້າຈາກ catalog ກາງ"}
        </p>
      </section>

      {!categoryId ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-8 text-center text-muted-foreground text-sm">
          {"ເລືອກໝວດເພື່ອເບິ່ງລາຍການສິນຄ້າ"}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
          <section className="flex min-h-0 flex-col rounded-xl border border-border bg-muted/10">
            <div className="flex items-center gap-2 border-border border-b px-3 py-2.5">
              <Tags className="size-4 shrink-0 text-primary" aria-hidden />
              <span className="font-medium text-foreground text-sm">
                {"ລາຍການສິນຄ້າ"}
              </span>
              {linkableQuery.isLoading ? (
                <Loader className="ms-auto size-4 text-muted-foreground" />
              ) : null}
            </div>
            <div className="border-border border-b px-3 py-2">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={"ຄົ້ນຊື່ສິນຄ້າ..."}
                  className="h-9 pl-9"
                />
              </div>
            </div>
            <ul className="max-h-72 min-h-48 overflow-y-auto p-2">
              {products.length === 0 && !linkableQuery.isLoading ? (
                <li className="px-2 py-6 text-center text-muted-foreground text-sm">
                  {"ບໍ່ພົບສິນຄ້າໃນໝວດນີ້"}
                </li>
              ) : null}
              {products.map((product) => (
                <ProductRow
                  key={product.catalogProductId}
                  product={product}
                  active={selectedProductId === product.catalogProductId}
                  onSelect={() => {
                    onSelectProduct(product.catalogProductId);
                    onCheckedSkusChange([]);
                  }}
                />
              ))}
            </ul>
          </section>

          <section className="flex min-h-0 flex-col rounded-xl border border-border bg-muted/10">
            <div className="flex items-center gap-2 border-border border-b px-3 py-2.5">
              <Package className="size-4 shrink-0 text-primary" aria-hidden />
              <span className="font-medium text-foreground text-sm">
                {"ເລືອກ OEM / SKU"}
              </span>
            </div>
            {!selectedProduct ? (
              <div className="flex flex-1 items-center justify-center px-4 py-10 text-center text-muted-foreground text-sm">
                {"ເລືອກສິນຄ້າຈາກລາຍການຊ້າຍ"}
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col p-3">
                <div className="mb-3">
                  <p className="font-medium text-foreground text-sm">
                    {selectedProduct.productName}
                  </p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {`${selectableSkus.length} OEM ພ້ອມຜູກ · ${selectedProduct.skus.filter((s) => s.alreadyLinked).length} ມີໃນຮ້ານແລ້ວ`}
                  </p>
                </div>

                {selectableSkus.length > 0 ? (
                  <label className="mb-2 flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
                    <Checkbox
                      checked={allSelectableChecked}
                      onCheckedChange={(v) => toggleAllSelectable(v === true)}
                    />
                    <span className="font-medium text-foreground text-sm">
                      {"ເລືອກທັງໝົດ"}
                    </span>
                  </label>
                ) : null}

                <ul className="max-h-56 space-y-1.5 overflow-y-auto">
                  {selectedProduct.skus.map((sku) => (
                    <li
                      key={sku.catalogSkuId}
                      className={cn(
                        "flex items-start gap-2 rounded-lg border px-3 py-2",
                        sku.alreadyLinked
                          ? "border-border/60 bg-muted/30 opacity-70"
                          : "border-border bg-background",
                      )}
                    >
                      <Checkbox
                        id={`pick-sku-${sku.catalogSkuId}`}
                        checked={checkedIds.has(sku.catalogSkuId)}
                        disabled={sku.alreadyLinked}
                        onCheckedChange={(v) => toggleSku(sku, v === true)}
                        className="mt-0.5"
                      />
                      <label
                        htmlFor={`pick-sku-${sku.catalogSkuId}`}
                        className={cn(
                          "min-w-0 flex-1",
                          !sku.alreadyLinked && "cursor-pointer",
                        )}
                      >
                        <p className="font-mono text-foreground text-sm">
                          {sku.oemPartNo?.trim() || "— (ບໍ່ມີ OEM)"}
                        </p>
                        <p className="mt-0.5 text-muted-foreground text-xs">
                          {conditionLabel(sku.condition)}
                          {sku.alreadyLinked ? " · ມີໃນຮ້ານແລ້ວ" : ""}
                        </p>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ProductRow({
  product,
  active,
  onSelect,
}: {
  product: LinkableCatalogProductRow;
  active: boolean;
  onSelect: () => void;
}) {
  const linkedCount = product.skus.filter((s) => s.alreadyLinked).length;
  const totalSkus = product.skus.length;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-start gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors",
          active
            ? "border-primary/40 bg-primary/8 ring-1 ring-primary/20"
            : "border-transparent hover:border-border hover:bg-background",
        )}
      >
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 font-medium text-foreground text-sm leading-snug">
            {product.productName}
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            {`${totalSkus} OEM${linkedCount > 0 ? ` · ${linkedCount} ມີໃນຮ້ານແລ້ວ` : ""}`}
          </p>
        </div>
      </button>
    </li>
  );
}
